import { filter, get, isEmpty, omit, difference, keys, find, compact, reduce, values, uniqBy } from 'lodash'

import moment from 'moment'

import {
  CLEAR_ORDER_SEARCH,
  CLEAR_SELECTED_ORDER,
  CREATE_OFFLINE_ORDER_SUCCEEDED,
  CREATE_OFFLINE_ORDER_FAILED,
  FILTER_ORDERS,
  REPLAY_AUTH_REQUEST_SUCCEEDED,
  REPLAY_AUTH_REQUEST_FAILED,
  SAVE_ORDER,
  SAVE_OFFLINE_ORDER,
  SEARCH_ORDER,
  SELECT_ORDER,
  SYNC_ORDER_INITIATED,
  SYNC_ORDER_SUCCEEDED,
  SYNC_ORDER_FAILED,
  SYNC_STATUS_RESET,
  UPDATE_ORDER_SUCCEEDED,
  UPDATE_ORDER_FAILED,
  VOID_ORDER_SUCCEEDED,
  VOID_ORDER_FAILED,
  ADD_SIGNATURE_TO_ORDER,
  ADD_TIP_TO_ORDER,
  PURGE_ORDERS,
  PURGE_ALL_ORDERS,
  LOAD_TAB_ORDERS_SUCCEEDED,
  LOAD_TAB_ORDERS_FAILED,
  STORE_ORDER_UUID_AND_ORDER_NUMBER,
  CLEAR_STORED_ORDER_UUID_AND_ORDER_NUMBER,
  REMOVE_ORDER,
  PERFORM_MAINTENANCE,
  UPDATE_MAINTENANCE_TIMESTAMP,
  SET_MAINTENANCE_ENABLED,
  SET_KIOSK_PAYMENT,
  UPDATE_ORDER_IN_PROGRESS,
  CLEAR_ORDER_IN_PROGRESS,
  ADD_TIP_ORDER_IN_PROGRESS,
  ADD_DONATION_ORDER_IN_PROGRESS,
  LOAD_ORDER_DETAILS_SUCCEEDED,
  LOAD_ORDER_DETAILS_FAILED,
  SET_ORDER_IN_PROGRESS,
  ADD_ITEMS_TO_TAB,
  ADD_ITEMS_TO_TAB_FAILED,
  ADD_ITEMS_TO_TAB_SUCCEEDED,
  ADD_ITEMS_TO_ORDER,
  REPLACE_ITEMS_FROM_ORDER,
  UPDATE_ORDER_TOTALS,
  RESET_MUTATION_IN_PROGRESS,
  REMOVE_QR_PAYMENT_AND_AUTHORIZATION_FAILED_STATUS,
  SET_ORDER_IN_PROGRESS_WITH_ID,
  RESTORE_QR_PAYMENT_AND_AUTHORIZATION_FAILED_STATUS,
  SET_QR_PAY_AUTH_ERROR,
  DUPLICATE_ORDER_WITH_NEW_IDS,
  SET_UPDATED_SINCE,
  ORDER_REPORTS_LAST_FETCHED_AT,
  CLEAR_TIP_ORDER_IN_PROGRESS,
  CLEAR_TIP_FROM_ORDER,
  SET_IS_ORDER_CANCELLED,
  TAB_REOPEN_SUCCEEDED,
  TAB_REOPEN_FAILED,
  TAB_REOPEN_REQUESTED,
  ADD_REOPEN_PAYMENT_TO_ORDER_IN_PROGRESS,
  REMOVE_PAYMENTS_FROM_ORDER_IN_PROGRESS,
  ADJUST_PRICE_ON_ODER_ITEM,
  PRICE_ADJUSTMENT_REQUESTED,
  PRICE_ADJUSTMENT_SUCCEEDED,
  PRICE_ADJUSTMENT_FAILED,
  SET_PAYMENT_COMPLETED,
  CLEAR_DONATION_FROM_ORDER,
  CLEAR_DONATION_ORDER_IN_PROGRESS,
  SET_CURRENT_ORDER_ID,
  EXEMPT_TAX_ON_ORDER,
  EXEMPT_TAX_ON_ORDER_SUCCEEDED,
  EXEMPT_TAX_ON_ORDER_FAILED,
  TAB_ORDERS_REQUESTED,
} from '../actions/order'

import { ON_RESET_SAF_FULL_RESPONSE, ON_SAF_FULL_RESPONSE_RECEIVED } from '../actions/peripheral'
import { SAVE_RICH_CHECKOUT_ORDER } from '../actions/richCheckout/order'
import {
  REQUEST_REFUND,
  REQUEST_TABBED_REFUND,
  REQUEST_TABBED_REFUND_SUCCEEDED,
  REQUEST_TABBED_REFUND_FAILED,
} from '../actions/refund'

import { getNumericSafValue } from '../selectors/peripheral'
import OrderStates, { INTERNAL_ORDER_STATES, STADIUM_ORDER_STATES } from '../utils/orderStates'
import { createOrderParams } from '../utils/orderUtils'

// TODO: populate the default state
const defaultState = {}

const safeAddToArray = (array, value) => {
  const arr = array || []

  if (arr.indexOf(value) < 0) return arr.concat(value)

  return arr
}

const safeRemoveFromArray = (array, value) => {
  const arr = array || []

  return filter(arr, (x) => x !== value)
}

const reducer = (state = defaultState, action) => {
  switch (action.type) {
    case CREATE_OFFLINE_ORDER_SUCCEEDED: {
      const { uuid } = action
      const order = state.byId[uuid]
      const offlineLocalIds = safeRemoveFromArray(state.offlineLocalIds, uuid)
      const successRemoteIds = safeAddToArray(state.successRemoteIds, uuid)

      return {
        ...state,
        offlineLocalIds,
        successRemoteIds,
        byId: {
          ...state.byId,
          [uuid]: {
            ...order,
            errorMessage: null,
            isSyncing: false,
            wasCreatedInStadium: true,
            needsSync: false,
            syncedAt: new Date(),
          },
        },
      }
    }

    case SET_IS_ORDER_CANCELLED: {
      const isCancelled = action.payload

      return {
        ...state,
        isCancelled,
      }
    }

    case SET_PAYMENT_COMPLETED: {
      const orderUuid = action.payload?.orderUuid
      const paymentCompleted = action.payload?.paymentCompleted || false
      const order = state.byId[orderUuid]

      if (!orderUuid || !order) return state

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderUuid]: {
            ...order,
            paymentCompleted,
          },
        },
      }
    }

    case CREATE_OFFLINE_ORDER_FAILED: {
      const { uuid, order } = action

      return {
        ...state,
        byId: {
          ...state.byId,
          [uuid]: {
            ...order,
            wasCreatedInStadium: false,
            isSyncing: false,
          },
        },
      }
    }

    case CLEAR_ORDER_SEARCH:
      return {
        ...state,
        ordersSearch: '',
      }

    case CLEAR_SELECTED_ORDER:
      return {
        ...state,
        selectedOrderId: null,
        currentOrderId: null,
      }

    case REMOVE_ORDER: {
      const { orderId } = action.payload
      const { [orderId]: value, ...byId } = state.byId

      return {
        ...state,
        byId: {
          ...byId,
        },
      }
    }

    case FILTER_ORDERS:
      return {
        ...state,
        ordersFilter: action.payload,
      }

    case SET_KIOSK_PAYMENT: {
      const { paymentType } = action.payload
      return {
        ...state,
        kioskPayment: paymentType,
      }
    }

    // Note: this action doesn't actually update order maybe it can be deprecated in favor of "SAVE_ORDER"
    case REPLAY_AUTH_REQUEST_SUCCEEDED: {
      const order = get(action, 'payload.order', {})
      const id = order.uuid
      const replayStatus = get(action, 'payload.replayStatus', { statusDisplay: 'No info while checking status' })

      // next step is to sync with stadium, which has been kicked off in the saga
      const offlineCardIds = safeRemoveFromArray(state.offlineCardIds, id)
      const activeOfflineCardCount = Math.max((state?.activeOfflineCardCount ?? 0) - 1, 0)

      return {
        ...state,
        offlineCardIds,
        activeOfflineCardCount,
        byId: {
          ...state.byId,
          [id]: {
            ...state.byId?.[id],
            replayStatus,
          },
        },
      }
    }

    case REPLAY_AUTH_REQUEST_FAILED: {
      const order = get(action, 'payload.order', {})
      const id = order.uuid
      const replayStatus = get(action, 'payload.replayStatus', { statusDisplay: 'No info while checking status' })
      const offlineLocalIds = !order.wasCreatedInStadium ? safeAddToArray(state.offlineLocalIds, id) : state.offlineLocalIds

      // TODO(mkramerl): Refactor state to utilize the state on the order, not the creation parameters.
      return {
        ...state,
        offlineLocalIds,
        byId: {
          ...state.byId,
          [id]: {
            ...state.byId[id],
            ...order,
            isSyncing: false,
            replayStatus,
          },
        },
      }
    }

    case SAVE_OFFLINE_ORDER: {
      const orderData = action.payload
      const uuid = orderData.uuid

      const offlineCardIds = safeAddToArray(state.offlineCardIds, uuid)
      const activeOfflineCardCount = Math.max((state?.activeOfflineCardCount ?? 0) + 1, 0)

      const offlineLocalIds = safeAddToArray(state.offlineLocalIds, uuid)

      return {
        ...state,
        offlineCardIds,
        activeOfflineCardCount,
        offlineLocalIds,
        byId: {
          ...state.byId,
          [uuid]: {
            ...state.byId[uuid],
            ...orderData,
          },
        },
        currentOrderId: uuid,
      }
    }

    case SAVE_ORDER: {
      const uuid = action.payload.uuid
      const needsSync = action.payload.needsSync ?? false
      const offlineLocalIds = action?.addToSyncArray ? safeAddToArray(state.offlineLocalIds, uuid) : state.offlineLocalIds

      let offlineCardIds = action?.removeFromCardSyncArray
        ? safeRemoveFromArray(state.offlineCardIds, uuid)
        : state.offlineCardIds

      if (action?.addToCardSyncArray) {
        offlineCardIds = safeAddToArray(offlineCardIds, uuid)
      }

      const reason = action?.payload?.reason || null

      return {
        ...state,
        offlineLocalIds,
        offlineCardIds,
        byId: {
          ...state.byId,
          [uuid]: {
            ...get(state, `byId.${uuid}`, {}),
            ...action.payload,
            modifiedAt: new Date(),
            reason,
            needsSync,
          },
        },
        currentOrderId: uuid,
      }
    }

    case SAVE_RICH_CHECKOUT_ORDER: {
      const { uuid } = action.payload

      const successRemoteIds = safeAddToArray(state.successRemoteIds, uuid)

      return {
        ...state,
        successRemoteIds,
        byId: {
          ...state.byId,
          [uuid]: {
            ...action.payload,
            isRichCheckout: true,
            modifiedAt: new Date(),
            syncedAt: new Date(),
            wasCreatedInStadium: true,
          },
        },
        currentOrderId: uuid,
      }
    }

    case SEARCH_ORDER:
      return {
        ...state,
        ordersSearch: action.payload,
      }

    case SELECT_ORDER:
      return {
        ...state,
        selectedOrderId: action.payload,
      }

    case SYNC_ORDER_INITIATED: {
      const id = action.id

      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: {
            ...state.byId[id],
            isSyncing: !state.byId[id]?.unsyncable,
          },
        },
      }
    }

    case SYNC_ORDER_SUCCEEDED: {
      const order = action.order || {}
      const id = order.uuid

      const successRemoteIds = safeAddToArray(state.successRemoteIds, id)
      const offlineLocalIds = safeRemoveFromArray(state.offlineLocalIds, id)

      let unsyncableIds = state.unsyncableIds
      // TODO: T41102 this is not clearing an unsyncable order if it's successful
      if (order.unsyncable) {
        unsyncableIds = safeRemoveFromArray(state.unsyncableIds, id)
      }

      return {
        ...state,
        offlineLocalIds,
        successRemoteIds,
        unsyncableIds,
        byId: {
          ...state.byId,
          [id]: {
            ...state.byId?.[id],
            ...order,
            errorMessage: null,
            isSyncing: false,
            needsSync: false,
            syncedAt: new Date(),
            wasCreatedInStadium: true,
            unsyncable: false,
          },
        },
      }
    }

    case SYNC_ORDER_FAILED: {
      let order = {}
      Object.assign(order, action.order)

      let needsSync = true
      const id = order.uuid
      order.isSyncing = false

      const response = get(action, 'payload.response', {})
      const status = get(response, 'status', null)

      const err = action?.payload
      const applicationMessage = err?.response?.data?.errorMessage ?? ''
      const friendlyMessage = err?.response?.data?.friendlyMessage ?? ''

      // The category is a more static determination of the issue.
      const errorCategory = (err?.response?.data?.errorCategories ?? [])[0]

      let errorMessage = friendlyMessage || applicationMessage || err.message

      errorMessage = errorMessage || get(response, 'data.errorMessage', null)
      if (err?.message === 'Network Error') {
        errorMessage = 'Network error while trying to submit order'
      }

      const wallet_nonce_authorization_failed =
        (errorCategory === 'AUTHORIZATION_FAILURE' || err.data?.state === 'authorization_failed') &&
        order.payments?.[0].paymentType === 'wallet_nonce'
      // QR pay auth failed specific processing
      if (wallet_nonce_authorization_failed) {
        order.orderState = INTERNAL_ORDER_STATES.FAILED
        order.state = STADIUM_ORDER_STATES.AUTHORIZATION_FAILED
        errorMessage = err.data?.stateDisplayName ?? err.response?.data?.stateDisplayName ?? errorMessage ?? undefined
        order.balanceDueInCents = order.amountInCents
      }

      order.errorMessage = errorMessage

      // An explicit authorization failure type response should be considered created in Stadium.
      if ((status === 422 || status === 408) && !wallet_nonce_authorization_failed) {
        order.wasCreatedInStadium = true
      }

      // prevent orders that were sync'ed from being marked as unsyncable
      if (state.successRemoteIds?.includes(id) || order.wasCreatedInStadium) {
        order.unsyncable = false
        needsSync = !!state.offlineLocalIds?.includes(id)
      }

      let unsyncableIds = state.unsyncableIds
      if (order.unsyncable) {
        unsyncableIds = safeAddToArray(state.unsyncableIds, id)
      }

      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: {
            ...state.byId[order.uuid],
            ...order,
            needsSync,
          },
        },
        unsyncableIds,
      }
    }

    case SYNC_STATUS_RESET: {
      const byId = reduce(
        state.byId,
        (ordersById, order) => ({
          ...ordersById,
          [order.uuid]: {
            ...state.byId[order.uuid],
            isSyncing: false,
          },
        }),
        {},
      )

      return {
        ...state,
        byId,
      }
    }

    case UPDATE_ORDER_SUCCEEDED: {
      const { uuid } = action
      const order = state.byId[uuid]
      const removeFromOfflineCards = action.options?.removeFromOfflineCards ?? true

      const payments = action.options?.updatedPayments || order.payments || []

      const offlineLocalIds = safeRemoveFromArray(state.offlineLocalIds, uuid)
      const offlineCardIds = removeFromOfflineCards ? safeRemoveFromArray(state.offlineCardIds, uuid) : state.offlineCardIds

      // orderStates of preauth or failure are initial states and must be removed in favor of final
      // states post-update
      // TODO(mkramerl): Don't use the intermediary orderState (part of legacy code), use the actual
      // order state given to us or known to us through card reader data.

      const clearableOrderStates = [INTERNAL_ORDER_STATES.PREAUTH, INTERNAL_ORDER_STATES.FAILED, INTERNAL_ORDER_STATES.CANCELLED]

      const updatedOrderState = values(clearableOrderStates).includes(order.orderState) ? undefined : order.orderState

      // move from offlineLocalIds to Completed
      return {
        ...state,
        offlineLocalIds,
        offlineCardIds,
        byId: {
          ...state.byId,
          [uuid]: {
            ...order,
            orderState: updatedOrderState,
            receiptSignature: null,
            errorMessage: null,
            isSyncing: false,
            needsSync: false,
            syncedAt: new Date(),
            payments,
          },
        },
      }
    }

    case UPDATE_ORDER_FAILED: {
      const { uuid, order, needsSync } = action
      const offlineLocalIds = safeAddToArray(state.offlineLocalIds, uuid)

      return {
        ...state,
        offlineLocalIds,
        byId: {
          ...state.byId,
          [uuid]: {
            ...state.byId?.[uuid],
            ...order,
            isSyncing: false,
            needsSync,
          },
        },
      }
    }

    case VOID_ORDER_SUCCEEDED: {
      const id = action.orderId
      const order = state.byId[id]
      const voidResponse = action.freedomPayResponse

      if (isEmpty(order)) return state

      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: {
            ...order,
            voidResponse,
            voidedAt: new Date(),
            voidFailedAt: null,
          },
        },
      }
    }

    case VOID_ORDER_FAILED: {
      const id = action.orderId
      const order = state.byId[id]

      // transtype is SALE for void failures. not sure it matters but just in case...
      const voidResponse = {
        ...action.freedomPayResponse,
        transType: 'VOID',
      }

      if (isEmpty(order)) return state

      return {
        ...state,
        byId: {
          ...state.byId,
          [id]: {
            ...order,
            voidResponse,
            voidFailedAt: new Date(),
          },
        },
      }
    }

    case ADD_SIGNATURE_TO_ORDER:
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.payload.orderUuid]: {
            ...state.byId[action.payload.orderUuid],
            receiptSignature: action.payload.signature,
          },
        },
      }

    case ADD_TIP_TO_ORDER: {
      const orderUuid = get(action, 'payload.orderUuid', '')
      const tipAmountInCents = get(action, 'payload.tipAmountInCents', 0)

      const order = get(state, `byId.${orderUuid}`, {})
      const amountInCents = get(order, `amountInCents`, 0)
      const newTotal = tipAmountInCents + amountInCents

      // not handling split payment types.
      let newPayment = get(order, ['payments', 0])
      if (!newPayment) newPayment = {}
      newPayment.amountInCents = newTotal

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderUuid]: {
            ...state.byId[orderUuid],
            amountInCents: newTotal,
            payments: [newPayment],
            tipAmountInCents: action.payload.tipAmountInCents,
          },
        },
      }
    }

    case PURGE_ORDERS: {
      return {
        ...state,
        byId: {
          ...omit(state.byId, action.payload.orderIds),
        },
        successRemoteIds: difference(state.successRemoteIds, action.payload.orderIds),
        purgedAt: new Date(),
      }
    }

    case PURGE_ALL_ORDERS: {
      return {
        ...state,
        byId: {},
        offlineCardIds: [],
        activeOfflineCardCount: 0,
        failureRemoteIds: [],
        offlineLocalIds: [],
        successRemoteIds: [],
        unsyncableIds: [],
        maintenancedAt: action.payload?.maintenancedAt ?? new Date(),
      }
    }

    case UPDATE_MAINTENANCE_TIMESTAMP: {
      return {
        ...state,
        maintenancedAt: action.payload.maintenanceTimestamp ?? new Date(),
      }
    }

    case SET_MAINTENANCE_ENABLED: {
      const maintenancedAt = action.payload.maintenanceEnabled ? new Date() : state.maintenancedAt

      return {
        ...state,
        maintenanceEnabled: action.payload.maintenanceEnabled,
        maintenancedAt: maintenancedAt,
      }
    }

    case PERFORM_MAINTENANCE: {
      return {
        ...state,
        byId: {
          ...omit(state.byId, action.payload.orderIds),
        },
        offlineCardIds: difference(state.offlineCardIds, action.payload.orderIds),
        failureRemoteIds: difference(state.failureRemoteIds, action.payload.orderIds),
        offlineLocalIds: difference(state.offlineLocalIds, action.payload.orderIds),
        successRemoteIds: difference(state.successRemoteIds, action.payload.orderIds),
        maintenancedAt: action.payload?.maintenancedAt ?? new Date(),
      }
    }

    //This reducer is not updating order state since the saga is refetching the updated order
    case REQUEST_TABBED_REFUND: {
      return {
        ...state,
        mutationInProgress: true,
      }
    }

    case REQUEST_TABBED_REFUND_SUCCEEDED: {
      return {
        ...state,
        mutationInProgress: false,
      }
    }

    case REQUEST_TABBED_REFUND_FAILED: {
      return {
        ...state,
        mutationInProgress: false,
      }
    }

    case REQUEST_REFUND: {
      const orderId = get(action, 'payload.orderId', '')
      const refundItemIndexes = get(action, 'payload.itemIndexes', [])
      const isCharitySelected = get(action, 'payload.isCharitySelected', false)
      const orderMenuItems = [...get(state, `byId.${orderId}.orderMenuItems`, [])]

      const newOrderMenuItems = [
        ...orderMenuItems.map((item, index) => {
          const isRefundItem = refundItemIndexes.includes(index)
          if (isRefundItem) return { ...item, refund: true }
          return item
        }),
      ]
      const newItemModels = [
        ...state.byId[orderId].itemModels.map((item, index) => {
          const isRefundItem = refundItemIndexes.includes(index)
          if (isRefundItem) return { ...item, refund: true }
          return item
        }),
      ]

      const charityAmountInCents = get(state, `byId.${orderId}`).charityAmountInCents

      const refundable = !newItemModels.some((item) => !item.refund)

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            orderMenuItems: [...newOrderMenuItems],
            itemModels: newItemModels,
            refundable,
            charityAmountInCents: isCharitySelected ? 0 : charityAmountInCents,
          },
        },
      }
    }

    case LOAD_TAB_ORDERS_SUCCEEDED: {
      const orders = state.byId
      const preOrders = get(action, 'payload', {})

      const updatedLocalOrders = reduce(
        orders,
        (updatedOrders, order) => {
          if (!preOrders[order?.uuid]) return updatedOrders
          const latestOrder = { ...preOrders[order?.uuid] }

          if (order?.orderState === INTERNAL_ORDER_STATES.CLOSED) {
            latestOrder.balanceDueInCents = 0
          }

          // if local has no tip and latest has tip, use latest tip
          if (!order.tipAmountInCents && latestOrder.state === STADIUM_ORDER_STATES.COMPLETION_PENDING) {
            const currentTipAmountInCents = latestOrder.paymentInfo.reduce((sum, payment) => sum + (payment.tip || 0), 0)
            latestOrder.tipAmountInCents = currentTipAmountInCents
          }

          updatedOrders[order?.uuid] = {
            ...orders[order?.uuid],
            ...latestOrder,
          }

          return updatedOrders
        },
        {},
      )

      const mergedOrders = { ...preOrders, ...updatedLocalOrders }

      keys(mergedOrders).forEach((orderId) => {
        const localItemModels = get(orders, `${orderId}.itemModels`, [])
        const remoteItemModels = get(preOrders, `${orderId}.itemModels`, [])

        // If this order doesn't have item models, or if there is no matching preorder, skip the next steps
        if (isEmpty(localItemModels) || isEmpty(remoteItemModels)) return

        let mergedItemModels = remoteItemModels.map((remoteItemModel) => {
          const maybedMatchItemModel = find(localItemModels, ['lineItemUuid', remoteItemModel.lineItemUuid])

          if (maybedMatchItemModel !== undefined && maybedMatchItemModel.refund === true) {
            return maybedMatchItemModel
          }

          if (maybedMatchItemModel !== undefined) {
            return remoteItemModel
          }

          return undefined
        })

        const cleanMergedItems = compact([...localItemModels, ...mergedItemModels])

        mergedOrders[orderId].itemModels = uniqBy(cleanMergedItems, 'lineItemUuid')
        mergedOrders[orderId].syncedAt = new Date()
        mergedOrders[orderId].modifiedAt = mergedOrders[orderId].updatedAt || mergedOrders[orderId].modifiedAt
        mergedOrders[orderId].latestFromServer = true
      })

      // Reset the remote syncing data when all orders are pulled since this is a complete sync.
      return {
        ...state,
        byId: {
          ...state.byId,
          ...mergedOrders,
        },
        orderDeltasSyncSuccessAt: null,
        unsyncedRemoteIds: [],
        preOrdersAreLoading: false,
        initialPreOrderLoadComplete: true,
        isLoading: false,
      }
    }
    case TAB_ORDERS_REQUESTED: {
      return {
        ...state,
        isLoading: true,
      }
    }

    case LOAD_TAB_ORDERS_FAILED: {
      return {
        ...state,
        isLoading: false,
      }
    }

    case LOAD_ORDER_DETAILS_SUCCEEDED: {
      const orders = state.byId
      const isRefundReceiptsEnabled = action?.payload?.isRefundReceiptsEnabled ?? false

      let order = {}
      Object.assign(order, action.payload?.order ?? {})

      order.latestFromServer = true
      order.isSyncing = false
      order.syncedAt = new Date()

      const shouldAddOfflineCardId =
        order.payments?.some((payment) => payment.userAttributes?.isOfflineApproval && !payment.userAttributes?.token) ?? false

      const offlineCardIds = shouldAddOfflineCardId ? safeAddToArray(state.offlineCardIds, order.uuid) : state.offlineCardIds

      if (order?.reopened && order.charityAmountInCents > 0) {
        order.amountInCents = order.amountInCents - order.charityAmountInCents
        order.balanceDueInCents = order?.balanceDueInCents ? order?.balanceDueInCents - order?.charityAmountInCents : null
        order.previousCharityAmountInCents = order.charityAmountInCents
        order.charityAmountInCents = 0
        order.lineItems = order.lineItems.filter((item) => item.type !== 'DonationLineItem')
        order.itemModels = order.itemModels.filter((item) => item.type !== 'DonationLineItem')
      } else {
        order.previousCharityAmountInCents = 0
      }

      const unsyncedRemoteIds = safeRemoveFromArray(state.unsyncedRemoteIds, order.uuid)

      // if placed on the same device, we have some data stored locally that we want to keep around
      // TODO - need to rely on backend for payment types and such
      if (OrderStates.isRemovable(order.state) && !isRefundReceiptsEnabled) {
        delete orders[order.uuid]
      } else {
        const localOrder = orders[order.uuid]

        if (localOrder?.orderState === INTERNAL_ORDER_STATES.CLOSED) {
          order.balanceDueInCents = 0
        }

        return {
          ...state,
          offlineCardIds,
          byId: {
            ...state.byId,
            [order.uuid]: {
              ...localOrder,
              ...order,
              state: order.state,
              pendingMutation: undefined,
              wasCreatedInStadium: true,
            },
          },
          isLoading: false,
          unsyncedRemoteIds,
        }
      }

      return {
        ...state,
        byId: {
          ...state.byId,
        },
        unsyncedRemoteIds,
        isLoading: false,
      }
    }

    case LOAD_ORDER_DETAILS_FAILED: {
      const orders = state.byId
      const orderUuid = get(action, 'payload.orderUuid', '')

      orders[orderUuid] = {
        ...orders[orderUuid],
        latestFromServer: false,
        isSyncing: false,
        syncedAt: null,
        pendingMutation: undefined,
      }

      return {
        ...state,
        byId: {
          ...state.byId,
        },
        isLoading: false,
      }
    }

    case STORE_ORDER_UUID_AND_ORDER_NUMBER: {
      const { orderUuid, orderNumber } = get(action, 'payload', '')

      return {
        ...state,
        storedOrderUuid: orderUuid,
        storedOrderNumber: orderNumber,
        byId: {
          ...state.byId,
        },
      }
    }

    case CLEAR_STORED_ORDER_UUID_AND_ORDER_NUMBER: {
      return {
        ...state,
        storedOrderUuid: undefined,
        storedOrderNumber: undefined,
        byId: {
          ...state.byId,
        },
      }
    }

    case SET_UPDATED_SINCE: {
      return {
        ...state,
        updatedSince: moment(),
      }
    }

    case SET_ORDER_IN_PROGRESS: {
      return {
        ...state,
        orderInProgress: action.payload,
      }
    }

    case UPDATE_ORDER_IN_PROGRESS: {
      return {
        ...state,
        orderInProgress: {
          ...state.orderInProgress,
          ...action?.payload,
        },
      }
    }

    case ADD_TIP_ORDER_IN_PROGRESS: {
      const tipAmountInCents = action?.payload ?? 0

      return {
        ...state,
        orderInProgress: {
          ...state.orderInProgress,
          tipAmountInCents: tipAmountInCents,
          tip: tipAmountInCents / 100 + '',
          balanceDueInCents: state?.orderInProgress?.balanceDueInCents + tipAmountInCents,
          amountInCents: state?.orderInProgress?.amountInCents + tipAmountInCents,
          hasTipApplied: true,
        },
      }
    }

    case ADD_DONATION_ORDER_IN_PROGRESS: {
      const donationAmountInCents = action?.payload ?? 0

      return {
        ...state,
        orderInProgress: {
          ...state.orderInProgress,
          charityAmountInCents: donationAmountInCents,
          balanceDueInCents: state?.orderInProgress?.balanceDueInCents + donationAmountInCents,
          amountInCents: state?.orderInProgress?.amountInCents + donationAmountInCents,
        },
      }
    }

    case CLEAR_DONATION_ORDER_IN_PROGRESS: {
      const curOrder = action?.payload?.uuid ? action?.payload : state?.orderInProgress
      return {
        ...state,
        orderInProgress: {
          ...curOrder,
          charityAmountInCents: 0,
          balanceDueInCents: curOrder?.balanceDueInCents - curOrder?.charityAmountInCents,
          amountInCents: curOrder?.amountInCents - curOrder?.charityAmountInCents,
        },
      }
    }

    case CLEAR_DONATION_FROM_ORDER: {
      const uuid = action?.payload?.uuid
      const order = state.byId[uuid]

      return {
        ...state,
        byId: {
          ...state.byId,
          [uuid]: {
            ...order,
            charityAmountInCents: 0,
            balanceDueInCents: order?.balanceDueInCents - order?.charityAmountInCents,
            amountInCents: order?.amountInCents - order?.charityAmountInCents,
          },
        },
      }
    }

    case CLEAR_TIP_ORDER_IN_PROGRESS: {
      // use action or state order in progress
      const curOrder = action?.payload?.uuid ? action?.payload : state?.orderInProgress

      return {
        ...state,
        orderInProgress: {
          ...curOrder,
          tipAmountInCents: 0,
          balanceDueInCents: curOrder?.balanceDueInCents - curOrder?.tipAmountInCents,
          amountInCents: curOrder?.amountInCents - curOrder?.tipAmountInCents,
          hasTipApplied: false,
        },
      }
    }

    case CLEAR_TIP_FROM_ORDER: {
      // use action or state order in progress
      const uuid = action?.payload?.uuid
      const order = state.byId[uuid]

      return {
        ...state,
        byId: {
          ...state.byId,
          [uuid]: {
            ...order,
            tipAmountInCents: 0,
            balanceDueInCents: order?.balanceDueInCents - order?.tipAmountInCents,
            amountInCents: order?.amountInCents - order?.tipAmountInCents,
            hasTipApplied: false,
          },
        },
      }
    }

    case CLEAR_ORDER_IN_PROGRESS: {
      return {
        ...state,
        orderInProgress: {},
      }
    }

    case ON_SAF_FULL_RESPONSE_RECEIVED: {
      const totalCount = getNumericSafValue(action?.payload?.SAFTotalCount)

      if (totalCount < 0) {
        return state
      }

      const uploadedCount = getNumericSafValue(action?.payload?.SAFUploadedCount)

      if (uploadedCount < 0) {
        return {
          ...state,
          activeOfflineCardCount: totalCount,
        }
      }

      return {
        ...state,
        activeOfflineCardCount: Math.max(totalCount - uploadedCount, 0),
      }
    }

    case ON_RESET_SAF_FULL_RESPONSE: {
      // Reset the active offline card count when resetting the SAF response.
      // If the offline card count is undfined, set it to 0 when resetting the card reader response;
      // otherwise, set it to the current active offline card count.
      // The active offline card count differs from the managed array.
      //   * The managed array is used to track orders which potentially need status updates.
      //   * The active offline card count is used to track the number of cards on the card reader
      //     which have yet to be synced. It will either align with the count of offline orders
      //     (i.e. the count of orders in the managed array) or be the result from the card reader.
      return {
        ...state,
        activeOfflineCardCount: state?.activeOfflineCardCount ?? 0,
      }
    }

    case ADD_ITEMS_TO_TAB: {
      return {
        ...state,
        mutationInProgress: true,
      }
    }

    case ADD_ITEMS_TO_TAB_SUCCEEDED: {
      const { orderId, userNotes } = action.payload

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            userNotes: userNotes,
          },
        },
        mutationInProgress: false,
      }
    }

    case ADD_ITEMS_TO_TAB_FAILED: {
      return {
        ...state,
        mutationInProgress: false,
      }
    }

    case RESET_MUTATION_IN_PROGRESS: {
      return {
        ...state,
        mutationInProgress: false,
      }
    }

    case ADD_ITEMS_TO_ORDER: {
      const {
        orderMenuItems,
        itemModels,
        orderId,
        subtotalAmountInCents,
        taxAmountInCents,
        amountInCents,
        balanceDueInCents,
        promotions,
        payments,
        serviceFee,
        taxRulesBreakdown,
        exemptableTaxRules,
      } = action.payload

      const updatedServiceFee = {
        name: state.byId[orderId].serviceFee.name,
        total: state.byId[orderId].serviceFee.total + serviceFee.total ?? 0,
        legal: state.byId[orderId].serviceFee.legal,
      }

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            subtotalAmountInCents: state.byId[orderId].subtotalAmountInCents + subtotalAmountInCents,
            taxAmountInCents: state.byId[orderId].taxAmountInCents + taxAmountInCents,
            amountInCents: state.byId[orderId].amountInCents + amountInCents,
            balanceDueInCents: state.byId[orderId].balanceDueInCents + balanceDueInCents,
            orderMenuItems: [...createOrderParams(state.byId[orderId]).orderMenuItems, ...orderMenuItems],
            promotions,
            payments,
            itemModels: [...state.byId[orderId].itemModels, ...itemModels],
            serviceFee: updatedServiceFee,
            taxRulesBreakdown,
            exemptableTaxRules,
          },
        },
      }
    }

    case REPLACE_ITEMS_FROM_ORDER: {
      const { orderId, newOrderMenuItems, newItemModels, needsSync = false } = action.payload

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            orderMenuItems: newOrderMenuItems,
            itemModels: newItemModels,
            needsSync,
          },
        },
      }
    }

    case UPDATE_ORDER_TOTALS: {
      const {
        orderId,
        subtotalAmountInCents,
        taxAmountInCents,
        amountInCents,
        balanceDueInCents,
        serviceFee,
        taxRulesBreakdown,
        exemptableTaxRules,
        charityAmountInCents,
      } = action.payload
      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            subtotalAmountInCents,
            taxAmountInCents,
            amountInCents,
            balanceDueInCents,
            serviceFee,
            taxRulesBreakdown,
            exemptableTaxRules: exemptableTaxRules || state.byId[orderId].exemptableTaxRules || {},
            charityAmountInCents: charityAmountInCents ?? state.byId[orderId].charityAmountInCents ?? 0,
          },
        },
      }
    }

    case REMOVE_QR_PAYMENT_AND_AUTHORIZATION_FAILED_STATUS: {
      const { orderId, balanceDueInCents } = action.payload

      //store previous values
      const previousAuthorizationFailedStatus = {
        state: state.byId[orderId]?.state,
        orderState: state.byId[orderId]?.orderState,
        errorMessage: state.byId[orderId]?.errorMessage,
        balanceDueInCents: state.byId[orderId]?.balanceDueInCents,
        payments: [...(state.byId[orderId]?.payments || [])],
        wasCreatedInStadium: true,
      }

      //remove QR pay from the payments array, if not removed it'll get repushed to stadium and it'll fail
      const payments = state.byId[orderId]?.payments?.filter((payment) => payment.paymentType !== 'wallet_nonce')

      const curOrderInProgress = { ...state.orderInProgress }

      if (curOrderInProgress && curOrderInProgress.uuid === orderId) {
        curOrderInProgress.balanceDueInCents = balanceDueInCents
      }

      return {
        ...state,
        orderInProgress: curOrderInProgress,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            orderState: 'open',
            errorMessage: '',
            balanceDueInCents,
            payments,
            needsSync: false,
            previousAuthorizationFailedStatus,
          },
        },
        QRPayAuthError: false,
      }
    }

    case RESTORE_QR_PAYMENT_AND_AUTHORIZATION_FAILED_STATUS: {
      const orderId = action.payload
      let offlineLocalIds = safeRemoveFromArray(state.offlineLocalIds, orderId)

      if (state.byId?.[orderId] === undefined) {
        return state
      }

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderId]: {
            ...state.byId[orderId],
            state: state.byId[orderId]?.previousAuthorizationFailedStatus.state,
            orderState: state.byId[orderId]?.previousAuthorizationFailedStatus.orderState,
            errorMessage: state.byId[orderId]?.previousAuthorizationFailedStatus.errorMessage,
            balanceDueInCents: state.byId[orderId]?.previousAuthorizationFailedStatus.balanceDueInCents,
            payments: state.byId[orderId]?.previousAuthorizationFailedStatus.payments,
            wasCreatedInStadium: state.byId[orderId]?.previousAuthorizationFailedStatus.wasCreatedInStadium,
          },
        },
        offlineLocalIds,
      }
    }

    case SET_ORDER_IN_PROGRESS_WITH_ID: {
      const orderId = action.payload

      const orderInProgress = { ...state.byId[orderId] }

      return {
        ...state,
        orderInProgress: orderInProgress,
      }
    }

    case SET_QR_PAY_AUTH_ERROR: {
      return {
        ...state,
        QRPayAuthError: action.payload,
      }
    }

    case DUPLICATE_ORDER_WITH_NEW_IDS: {
      const { oldOrderId, newOrderId, newOrderNumber } = action.payload
      const offlineLocalIds = safeRemoveFromArray(state.offlineLocalIds, oldOrderId)

      return {
        ...state,
        byId: {
          ...state.byId,
          [newOrderId]: {
            ...state.byId[oldOrderId],
            uuid: newOrderId,
            orderNumber: newOrderNumber,
            isSyncing: false,
            previousAuthorizationFailedStatus: null,
            balanceDueInCents: 0,
            orderState: INTERNAL_ORDER_STATES.CLOSED,
            state: STADIUM_ORDER_STATES.COMPLETED,
          },
        },
        offlineLocalIds,
      }
    }

    case ORDER_REPORTS_LAST_FETCHED_AT: {
      return {
        ...state,
        orderReportsLastFetchedAt: action.payload ?? moment().format('MM/DD/YY h:mm A'),
      }
    }

    case TAB_REOPEN_REQUESTED: {
      return {
        ...state,
        mutationInProgress: true,
      }
    }

    case TAB_REOPEN_SUCCEEDED: {
      const orderUuid = action.payload.orderUuid
      const reopenState = action.payload.reopenState
      const balanceDueInCents = action.payload.balanceDueInCents
      const curOrder = state.byId[orderUuid]
      const additionalProps = {}
      if (curOrder.charityAmountInCents > 0) {
        additionalProps.previousCharityAmountInCents = curOrder.charityAmountInCents
        additionalProps.amountInCents = curOrder.amountInCents - curOrder.charityAmountInCents
        additionalProps.balanceDueInCents = balanceDueInCents ? balanceDueInCents - additionalProps?.charityAmountInCents : null
        additionalProps.charityAmountInCents = 0
        additionalProps.lineItems = curOrder.lineItems.filter((item) => item.type !== 'DonationLineItem')
        additionalProps.itemModels = curOrder.itemModels.filter((item) => item.type !== 'DonationLineItem')
      }

      //TODO: T40612 need to update order total without the tip
      return {
        ...state,
        byId: {
          ...state.byId,
          [orderUuid]: {
            ...state.byId[orderUuid],
            state: reopenState,
            orderState: '',
            payments: [],
            paymentCompleted: false,
            tipAmountInCents: 0,
            tip: '0.00',
            balanceDueInCents,
            isRemotePayment: false,
            remainingBalance: '0.00',
            reopened: true,
            creditCardAuthAdded: false,
            isApprovedOffline: false,
            isClosedTab: false,
            displayName: '',
            paymentDisplay: '',
            userAttributes: undefined,
            cardReaderData: undefined,
            isMultiPayment: false,
            hasTipApplied: false,
            affiliations: undefined,
            remainingTicketBalance: undefined,
            ...additionalProps,
          },
        },
        mutationInProgress: false,
      }
    }

    case TAB_REOPEN_FAILED: {
      return {
        ...state,
        mutationInProgress: false,
      }
    }

    case ADD_REOPEN_PAYMENT_TO_ORDER_IN_PROGRESS: {
      const { payment } = action.payload
      const payments = state.orderInProgress?.payments || []
      const userAttributes = payment?.userAttributes || undefined

      return {
        ...state,
        orderInProgress: {
          ...state.orderInProgress,
          payments: [...payments, payment],
          creditCardAuthAdded: true,
          userAttributes,
        },
      }
    }

    case REMOVE_PAYMENTS_FROM_ORDER_IN_PROGRESS: {
      return {
        ...state,
        orderInProgress: {
          ...state.orderInProgress,
          payments: [],
          creditCardAuthAdded: false,
        },
      }
    }

    case PRICE_ADJUSTMENT_REQUESTED: {
      return {
        ...state,
        mutationInProgress: true,
      }
    }

    case PRICE_ADJUSTMENT_SUCCEEDED: {
      return {
        ...state,
        mutationInProgress: false,
      }
    }

    case PRICE_ADJUSTMENT_FAILED: {
      return {
        ...state,
        mutationInProgress: false,
      }
    }

    case ADJUST_PRICE_ON_ODER_ITEM: {
      const { orderUuid, lineItemUuid, price, formattedPrice, priceInCents, originalPriceInCents, managerId, reason } =
        action.payload

      const orderMenuItems = (state.byId[orderUuid]?.orderMenuItems || []).map((orderMenuItem) => {
        if (orderMenuItem.uuid === lineItemUuid) {
          return {
            ...orderMenuItem,
            price,
            reason,
            approverUuid: managerId,
          }
        }
        return orderMenuItem
      })

      const itemModels = (state.byId[orderUuid]?.itemModels || []).map((itemModel) => {
        if (itemModel.lineItemUuid === lineItemUuid) {
          return {
            ...itemModel,
            price: formattedPrice,
            priceInCents,
            managerId,
            reason,
            originalPriceInCents,
          }
        }
        return itemModel
      })

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderUuid]: {
            ...state.byId[orderUuid],
            itemModels,
            orderMenuItems,
          },
        },
      }
    }

    case SET_CURRENT_ORDER_ID: {
      return {
        ...state,
        currentOrderId: action.payload,
      }
    }

    case EXEMPT_TAX_ON_ORDER: {
      const orderUuid = action.payload.orderUuid
      const taxExemptReason = action.payload.taxExemptReason || ''
      const taxExemptApproverUuid = action.payload.taxExemptApproverUuid || ''

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderUuid]: {
            ...state.byId[orderUuid],
            taxExempt: true,
            taxExemptReason,
            taxExemptApproverUuid,
          },
        },
        mutationInProgress: true,
      }
    }

    case EXEMPT_TAX_ON_ORDER_SUCCEEDED: {
      return {
        ...state,
        mutationInProgress: false,
      }
    }

    case EXEMPT_TAX_ON_ORDER_FAILED: {
      const orderUuid = action.payload.orderUuid

      return {
        ...state,
        byId: {
          ...state.byId,
          [orderUuid]: {
            ...state.byId[orderUuid],
            taxExempt: false,
            taxExemptReason: '',
            taxExemptApproverUuid: '',
          },
        },
        mutationInProgress: false,
      }
    }

    default:
      return state
  }
}

export default reducer
